// The shared, authenticated backend client lives in src/lib/apiFetch.ts. It is
// re-exported here so the partner-reconciliation call sites keep their existing
// `from './api'` imports.
export { apiFetch, apiFetchBlob, apiFetchResponse, apiUpload, API_URL } from '../lib/apiFetch';

export type PartnerType = 'transport' | 'crematorium' | 'dropoff_location';

/** Which partners.is_* capability flag corresponds to each invoice role. */
export const PARTNER_TYPE_ROLE_FIELD: Record<PartnerType, string> = {
  transport: 'is_transport',
  crematorium: 'is_crematorium',
  dropoff_location: 'is_dropoff_location',
};

export const PARTNER_TYPE_LABELS: Record<PartnerType, string> = {
  transport: 'Transport company',
  crematorium: 'Crematorium',
  dropoff_location: 'Drop-off facility',
};
export type InvoiceStatus = 'extracting' | 'review' | 'reconciled' | 'paid' | 'failed';
export type MatchStatus = 'matched' | 'ambiguous' | 'unmatched' | 'manual';

export interface MatchCandidate {
  funeral_id: number;
  deceased_name: string;
  date_of_death: string | null;
  similarity: number;
  partner_assigned: boolean;
}

export interface LineItem {
  id: string;
  description: string;
  amount: number | string | null;
  tax: number | string | null;
}

export interface InvoiceCase {
  id: string;
  deceased_name_raw: string;
  partner_case_ref: string | null;
  service_date: string | null;
  case_subtotal: number | string | null;
  case_tax: number | string | null;
  case_total: number | string | null;
  matched_funeral_id: number | null;
  match_status: MatchStatus;
  match_confidence: number | null;
  match_method: string | null;
  match_candidates: MatchCandidate[] | null;
  reconciled: boolean;
  line_items: LineItem[];
  duplicate_in_invoice?: boolean;
  duplicate_other_invoices?: Array<{ invoice_id: string; status: string }>;
  matched_funeral?: {
    id: number;
    date_time_of_death: string | null;
    deceased_client?: { name: string } | null;
  } | null;
}

export interface PartnerInvoice {
  id: string;
  partner_id: string | null;
  partner_type: PartnerType | null;
  invoice_number: string | null;
  invoice_date: string | null;
  period_start: string | null;
  period_end: string | null;
  currency: string;
  extracted_subtotal: number | string | null;
  extracted_tax: number | string | null;
  extracted_total: number | string | null;
  tax_breakdown: Record<string, number> | null;
  status: InvoiceStatus;
  payment_method: 'cheque' | 'credit_card' | null;
  payment_reference: string | null;
  paid_at: string | null;
  extraction_error: string | null;
  file_name: string | null;
  source_files: Array<{ path: string; name: string | null; mime: string | null }> | null;
  created_at: string;
  partner?: { id: string; name: string; type: string } | null;
  cases?: InvoiceCase[];
  case_count?: number;
  matched_count?: number;
  reconciled_count?: number;
  /**
   * Sum of the case lines. Tax-inclusive or not depending on how the partner
   * bills, so it is not necessarily what Cleo owes — see invoice_total.
   * Null when an invoice has no cases yet (extraction pending or failed).
   */
  cases_total_sum?: number | null;
  /** What Cleo owes: the document total unless it disagrees with the case lines. */
  invoice_total?: number | null;
  line_amounts_sum?: number;
  computed_total?: number;
  /** False when the document's stated total disagrees with the case lines. */
  total_matches_extracted?: boolean | null;
}

export function money(value: number | string | null | undefined, currency = 'cad'): string {
  if (value == null || value === '') return '—';
  const n = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: currency.toUpperCase() }).format(n);
}

/** Date-only value, no time or zone — invoice_date, period_*, service_date. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    // `new Date('2025-02-26')` is parsed as UTC midnight, which renders as the
    // 25th anywhere west of Greenwich. Build date-only values from their parts
    // so they land on local midnight and display the day the partner wrote.
    // Timestamps (created_at, paid_at) are genuine instants — parse as given.
    const date = DATE_ONLY.test(dateStr)
      ? new Date(Number(dateStr.slice(0, 4)), Number(dateStr.slice(5, 7)) - 1, Number(dateStr.slice(8, 10)))
      : new Date(dateStr);
    return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

// --- "By partner & month" (T-082) ------------------------------------------------

export type CellTone = 'ok' | 'warn' | 'mute' | 'bad' | 'none';

export interface MonthCell {
  sent: number;
  billed: number;
  tone: CellTone;
  note: string;
  ledger_total: number | null;
}

export interface PartnerMonthsRow {
  partner_id: string;
  name: string;
  roles: Array<'transport' | 'crematorium' | 'dropoff'>;
  /** Invoices itself from the partner portal's drop-off ledger. */
  self_invoicing: boolean;
  cells: Record<string, MonthCell>;
}

export interface PartnerMonthsGrid {
  months: string[];
  partners: PartnerMonthsRow[];
}

export interface SnapshotLine {
  key: string;
  sku: string;
  product: string;
  qty: number | null;
  rate: number | null;
  amount: number;
  taxExempt?: boolean;
  cases?: Array<{ id: number; name: string; note: string }>;
}

export interface DropoffBatch {
  id: string;
  month: string;
  marked_at: string;
  marked_by: string | null;
  case_count: number;
  lines: SnapshotLine[];
  taxable_subtotal: number;
  exempt_subtotal: number;
  hst: number;
  total: number;
}

export interface BillDiffRow {
  key: string;
  sku: string | null;
  product: string;
  their_qty: number | null;
  their_amount: number;
  ledger_qty: number | null;
  ledger_amount: number;
  difference: number;
  tax_exempt: boolean;
}

export interface PartnerMonthDetail {
  partner: { id: string; name: string; self_invoicing: boolean };
  month: string;
  cases: Array<{
    id: number;
    name: string;
    roles: string[];
    date: string | null;
    billed_on: Array<{
      invoice_id: string;
      invoice_number: string | null;
      status: string;
      reconciled: boolean;
    }>;
    dropoff_invoice_id: string | null;
  }>;
  not_billed: Array<{ id: number; name: string; date: string | null }>;
  invoices: Array<{
    id: string;
    invoice_number: string | null;
    invoice_date: string | null;
    period_start: string | null;
    period_end: string | null;
    status: InvoiceStatus;
    extracted_total: number | string | null;
    matched_in_month: number;
  }>;
  portal: null | {
    rate_card: string;
    batches: DropoffBatch[];
    not_marked: number;
    comparisons: Array<{
      invoice_id: string;
      invoice_number: string | null;
      invoice_date: string | null;
      covered_months: string[];
      batches: number;
      ledger_cases: number;
      rows: BillDiffRow[];
      theirs: {
        subtotal: number | null;
        tax: number | null;
        total: number | null;
      };
      ledger: { subtotal: number; tax: number; total: number };
    }>;
  };
}

/** "2026-09" -> "September 2026". */
export function monthName(key: string): string {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 15).toLocaleDateString('en-CA', {
    month: 'long',
    year: 'numeric',
  });
}
